import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, from } from 'rxjs';
import { MsalAuthService } from './msal-auth.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, map, tap } from 'rxjs/operators';
import { ApiConfigService } from './api-config.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private loggedIn = new BehaviorSubject<boolean>(false);
  private apiToken: string | null = null;

  constructor(
    private router: Router,
    private msalAuthService: MsalAuthService,
    private http: HttpClient,
    private apiConfig: ApiConfigService
  ) {
    this.initializeAuth();
  }

  private async initializeAuth() {
    try {
      const token = this.getTokenFromUrl() || localStorage.getItem('token');
      if (token) {
        localStorage.setItem('token', token);
        this.apiToken = token;
        this.loggedIn.next(true);
      } else {
        // Try to get a fresh token
        const msalToken = await this.msalAuthService.getToken();
        if (msalToken) {
          await this.exchangeToken(msalToken);
        }
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
    }
  }

  private async exchangeToken(msalToken: string): Promise<boolean> {
    try {
      console.log('Exchanging MSAL token for API token');
      const headers = new HttpHeaders().set('Authorization', `Bearer ${msalToken}`);
      
      const response = await this.http.post<{ token: string }>(
        `${this.apiConfig.authUrl}/token`, 
        {}, 
        { headers }
      ).toPromise();
      
      if (response && response.token) {
        console.log('Token exchange successful');
        this.apiToken = response.token;
        localStorage.setItem('token', response.token);
        this.loggedIn.next(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token exchange failed:', error);
      return false;
    }
  }

  getTokenFromUrl(): string | null {
    const url = window.location.href;
    const hashIndex = url.indexOf('#');
    
    if (hashIndex === -1) {
      return null;
    }
  
    const fragment = url.substring(hashIndex + 1);
    const fragmentIndex = fragment.indexOf('?');
    
    if (fragmentIndex === -1) {
      return null;
    }
  
    const paramsString = fragment.substring(fragmentIndex + 1);
    const params = new URLSearchParams(paramsString);
    const token = params.get('token');
    
    if (token) {
      // Remove token from URL
      params.delete('token');
      const newFragment = fragment.substring(0, fragmentIndex + 1) + params.toString();
      const newUrl = url.split('#')[0] + '#' + newFragment;
      window.history.replaceState({}, document.title, newUrl);
    }
    
    return token;
  }  

  async logout(): Promise<void> {
    try {
      // Clear local tokens
      localStorage.removeItem('token');
      this.apiToken = null;
      this.loggedIn.next(false);
      
      // Log out from MSAL
      await this.msalAuthService.logout();
      
      console.log('Logged out successfully');
    } catch (error) {
      console.error('Error during logout:', error);
      localStorage.removeItem('token');
      this.loggedIn.next(false);
      this.router.navigate(['/login']);
    }
  }

  isLoggedIn(): Observable<boolean> {
    return this.loggedIn.asObservable();
  }

  getToken(): string | null {
    if (!this.apiToken) {
      this.apiToken = localStorage.getItem('token');
    }
    
    if (!this.apiToken) {
      // If no token is found, try to get a new one
      this.msalAuthService.getToken().then(msalToken => {
        if (msalToken) {
          this.exchangeToken(msalToken);
        }
      }).catch(error => {
        console.error('Error getting MSAL token:', error);
      });
    }
    
    return this.apiToken;
  }
}